import React, { useMemo } from "react";
import SwitchToggle from "../../components/SwitchToggle";
import { useNavigate, useLocation } from "react-router-dom";
import { useGetProgram, useGetWeeklyPrograms, useGetWorkouts } from "../../api/hooks/useWorkout";
import { useGetSettings } from "../../api/hooks/useSettings";
import { Box, CircularProgress, Button } from "@mui/material";
import { format } from "date-fns";
import { DIFFICULTY_LEVEL, VISIBILITY_SCOPE, WORKOUT_ATTRIBUTES } from "../../constants/settings";
import { showError } from "../../helpers/toast";

const ViewProgram = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const programId = (location.state as any)?.programId || "";

  const { data: programData, isLoading, error } = useGetProgram(programId, { enabled: !!programId });
  const { data: weeklyProgramsData, isLoading: isLoadingWeekly, error: weeklyError } = useGetWeeklyPrograms(
    programId,
    { enabled: !!programId }
  );

  // Extract weekly programs list from response
  const weeklyPrograms = useMemo(() => {
    const data = weeklyProgramsData?.data;
    if (data && typeof data === "object") {
      if (Array.isArray(data.list)) return data.list;
      if (Array.isArray(data)) return data;
    }
    return [];
  }, [weeklyProgramsData]);

  // Collect all unique workout IDs from weekly programs
  const workoutIds = useMemo(() => {
    const ids = new Set<string>();
    weeklyPrograms.forEach((week: any) => {
      if (week.days && Array.isArray(week.days)) {
        week.days.forEach((day: any) => {
          if (day.workoutId) ids.add(day.workoutId);
        });
      }
    });
    return Array.from(ids);
  }, [weeklyPrograms]);

  // Fetch workouts for all workout IDs
  const { data: workoutsData } = useGetWorkouts(
    { page: 1, limit: 100, offset: 0 },
    { enabled: workoutIds.length > 0 }
  );

  // Create workout map for quick lookup
  const workoutsMap = useMemo(() => {
    const workoutsList = workoutsData?.data;
    let workouts: any[] = [];
    if (Array.isArray(workoutsList)) {
      workouts = workoutsList;
    } else if (workoutsList && typeof workoutsList === "object") {
      if (Array.isArray(workoutsList.list)) workouts = workoutsList.list;
    }
    return workouts.reduce(
      (acc: Record<string, any>, workout: any) => ({
        ...acc,
        [workout._id]: workout,
      }),
      {}
    );
  }, [workoutsData]);

  // Fetch equipment and goal outcome settings for name lookup
  const equipmentParams = useMemo(() => ({ type: WORKOUT_ATTRIBUTES.EQUIPMENT }), []);
  const { data: equipmentData } = useGetSettings(equipmentParams, { enabled: true });

  const goalOutcomeParams = useMemo(() => ({ type: WORKOUT_ATTRIBUTES.GOAL_OUTCOME }), []);
  const { data: goalOutcomeData } = useGetSettings(goalOutcomeParams, { enabled: true });

  const program = (programData?.data || {}) as any;

  const normalizeList = (raw: any) => {
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === "object") {
      if (Array.isArray(raw.list)) return raw.list;
      if (Array.isArray(raw.data)) return raw.data;
    }
    return [];
  };

  const equipmentOptions = useMemo(() => normalizeList(equipmentData?.data), [equipmentData]);
  const goalOutcomeOptions = useMemo(() => normalizeList(goalOutcomeData?.data), [goalOutcomeData]);

  const equipmentMap = useMemo(
    () =>
      equipmentOptions.reduce(
        (acc: Record<string, string>, item: any) => ({
          ...acc,
          [item._id]: item.name,
        }),
        {}
      ),
    [equipmentOptions]
  );

  const goalOutcomeMap = useMemo(
    () =>
      goalOutcomeOptions.reduce(
        (acc: Record<string, string>, item: any) => ({
          ...acc,
          [item._id]: item.name,
        }),
        {}
      ),
    [goalOutcomeOptions]
  );

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd MMM yyyy");
    } catch {
      return "N/A";
    }
  };

  const formatDifficulty = (val?: number | number[]) => {
    const toCodes = (v: any) => {
      if (Array.isArray(v)) return v.map((x) => Number(x)).filter(Number.isFinite);
      const n = Number(v);
      return Number.isFinite(n) ? [n] : [];
    };

    const codes = toCodes(val);
    if (!codes.length) return "N/A";

    const labels = codes
      .map((code) => {
        if (code === DIFFICULTY_LEVEL.BEGGINER) return "Beginner";
        if (code === DIFFICULTY_LEVEL.BEGINNER_INTERMEDIATE) return "Beginner-Intermediate";
        if (code === DIFFICULTY_LEVEL.INTERMEDIATE) return "Intermediate";
        if (code === DIFFICULTY_LEVEL.ADVANCED) return "Advanced";
        return null;
      })
      .filter(Boolean) as string[];

    return labels.length ? labels.join(", ") : "N/A";
  };

  const formatVisibilityScope = (val?: number) => {
    if (val === VISIBILITY_SCOPE.DEMAND) return "Demand";
    if (val === VISIBILITY_SCOPE.PROGRAM) return "Program";
    if (val === VISIBILITY_SCOPE.BOTH) return "Both";
    return "N/A";
  };

  const formatArray = (arr?: any[], map?: Record<string, string>) => {
    if (!arr || !arr.length) return "N/A";

    // Check if array contains objects with name property
    if (typeof arr[0] === 'object' && arr[0] !== null && 'name' in arr[0]) {
      return arr.map((item) => item.name).filter(Boolean).join(", ");
    }

    // Otherwise treat as array of IDs
    if (map) {
      return arr.map((id) => map[id] || id).filter(Boolean).join(", ");
    }
    return arr.join(", ");
  };

  React.useEffect(() => {
    if (error) {
      showError((error as any)?.response?.data?.message || "Failed to load program details");
    }
  }, [error]);

  React.useEffect(() => {
    if (!programId && !isLoading) {
      navigate("/manage-programs");
    }
  }, [programId, isLoading, navigate]);

  if (isLoading) {
    return (
      <>
        <div className="main_title">
          <h1>Program Details</h1>
          <p>
            <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
            <button onClick={() => navigate("/manage-programs")}>Manage Programs</button> - View Program
          </p>
        </div>
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="main_title">
          <h1>Program Details</h1>
          <p>
            <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
            <button onClick={() => navigate("/manage-programs")}>Manage Programs</button> - View Program
          </p>
        </div>
        <Box display="flex" justifyContent="center" alignItems="center" p={4} flexDirection="column">
          <p>Failed to load program details.</p>
          <Button onClick={() => navigate("/manage-programs")} style={{ marginTop: 10 }}>
            Back to Programs
          </Button>
        </Box>
      </>
    );
  }

  return (
    <>
      <div className="main_title">
        <h1>Program Details</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-programs")}>Manage Programs</button> - View Program
        </p>
      </div>

      <div className="cards">
        <div className="view_page">
          <div className="gap_p">
            {/* Thumbnail */}
            <div className="w_100">
              <h6 style={{ fontWeight: "bold" }}>Program Thumbnail</h6>
              <img
                src={program.thumnail || "/static/images/program_placeholder.png"}
                alt={program.title || "Program"}
                style={{ width: "100%", maxHeight: "400px", objectFit: "contain", borderRadius: "8px" }}
              />
            </div>

            {/* Title */}
            <div className="w_33">
              <h6 style={{ fontWeight: "bold" }}>Program Title</h6>
              <p>{program.title || "N/A"}</p>
            </div>

            {/* Sub Title */}
            <div className="w_33">
              <h6 style={{ fontWeight: "bold" }}>Sub Title</h6>
              <p>{program.subTitle || "N/A"}</p>
            </div>

            {/* Unique Id */}
            <div className="w_33">
              <h6 style={{ fontWeight: "bold" }}>Unique Id</h6>
              <p>{program.uniqueId || "N/A"}</p>
            </div>

            {/* Duration in Weeks */}
            <div className="w_33">
              <h6 style={{ fontWeight: "bold" }}>Duration in Weeks</h6>
              <p>
                {program.durationInWeeks
                  ? `${program.durationInWeeks} ${Number(program.durationInWeeks) === 1
                    ? "Week"
                    : "Weeks"
                  }`
                  : "N/A"}
              </p>
            </div>

            {/* Days Per Week */}
            <div className="w_33">
              <h6 style={{ fontWeight: "bold" }}>Days Per Week</h6>
              <p>{program.daysPerWeek || "N/A"}</p>
            </div>

            {/* Level */}
            <div className="w_33">
              <h6 style={{ fontWeight: "bold" }}>Level</h6>
              <p>{formatDifficulty(program.difficultyLevel)}</p>
            </div>

            {/* Visibility Scope */}
            <div className="w_33">
              <h6 style={{ fontWeight: "bold" }}>Visibility Scope</h6>
              <p>{formatVisibilityScope(program.visibilityScope)}</p>
            </div>

            {/* Enrolled Users */}
            <div className="w_33">
              <h6 style={{ fontWeight: "bold" }}>Enrolled Users</h6>
              <p>{program.enrolledUsers || 0}</p>
            </div>

            {/* Is Published */}
            <div className="w_33">
              <h6 style={{ fontWeight: "bold" }}>Published Status</h6>
              <p>
                <SwitchToggle defaultChecked={program.isPublished || false} disabled />
              </p>
            </div>

            {/* Created Date */}
            <div className="w_33">
              <h6 style={{ fontWeight: "bold" }}>Created Date</h6>
              <p>{formatDate(program.createdAt)}</p>
            </div>

            {/* Updated Date */}
            <div className="w_33">
              <h6 style={{ fontWeight: "bold" }}>Updated Date</h6>
              <p>{formatDate(program.updatedAt)}</p>
            </div>

            {/* Description */}
            <div className="w_100">
              <h6 style={{ fontWeight: "bold" }}>Description</h6>
              {program.description ? (
                <div
                  className="rich-content"
                  style={{ display: "block" }}
                  dangerouslySetInnerHTML={{ __html: program.description }}
                />
              ) : (
                <p>N/A</p>
              )}
            </div>

            {/* Equipment Needed */}
            <div className="w_100">
              <h6 style={{ fontWeight: "bold" }}>Equipment Needed</h6>
              <p>{formatArray(program.equipment, equipmentMap)}</p>
            </div>

            {/* Goal */}
            <div className="w_100">
              <h6 style={{ fontWeight: "bold" }}>Goal</h6>
              <p>{formatArray(program.goalOutcome, goalOutcomeMap)}</p>
            </div>

            {/* Weeks & Days */}
            <div className="w_100">
              <h6 style={{ fontWeight: "bold" }}>Program Weeks & Days</h6>
              {isLoadingWeekly ? (
                <Box display="flex" justifyContent="center" alignItems="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : weeklyError ? (
                <p style={{ color: "#d32f2f", marginTop: "10px" }}>
                  Failed to load weekly program details
                </p>
              ) : weeklyPrograms.length > 0 ? (
                <div style={{ marginTop: "10px" }}>
                  {weeklyPrograms.map((week: any, weekIndex: number) => (
                    <div
                      key={week._id || weekIndex}
                      style={{
                        marginBottom: "20px",
                        padding: "15px",
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                      }}
                    >
                      <h6 style={{ marginBottom: "10px", fontWeight: "bold" }}>
                        Week {week.week || weekIndex + 1}
                      </h6>
                      {week.days && Array.isArray(week.days) && week.days.length > 0 ? (
                        <div>
                          {week.days.map((day: any, dayIndex: number) => {
                            // Check if workout is embedded in the response or needs to be looked up
                            const workout = day.workout && typeof day.workout === 'object'
                              ? day.workout
                              : (day.workoutId ? (workoutsMap as Record<string, any>)[day.workoutId] : null);

                            return (
                              <div
                                key={day._id || dayIndex}
                                style={{
                                  margin: "10px 0",
                                  padding: "10px",
                                  backgroundColor: "#f5f5f5",
                                  borderRadius: "4px",
                                }}
                              >
                                <p style={{ margin: "5px 0", fontWeight: "500" }}>
                                  Day {day.day || dayIndex + 1}
                                </p>
                                {workout ? (
                                  <div style={{ paddingLeft: "15px" }}>
                                    <p style={{ margin: "3px 0" }}>
                                      <strong>Workout:</strong> {workout.title || workout.name || "N/A"}
                                    </p>
                                    {workout.duration && (
                                      <p style={{ margin: "3px 0", color: "#666" }}>
                                        Duration: {workout.duration} minutes
                                      </p>
                                    )}
                                    {workout.difficultyLevel && (
                                      <p style={{ margin: "3px 0", color: "#666" }}>
                                        Difficulty: {formatDifficulty(workout.difficultyLevel)}
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <p style={{ margin: "5px 0", paddingLeft: "15px", color: "#666" }}>
                                    Workout: N/A
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p style={{ color: "#999" }}>No days configured for this week</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: "#999", marginTop: "10px" }}>No weekly program data available</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewProgram;
